import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import { breakpoints } from 'javascripts/shared/utils/breakpoint_utils';
import { bubEvents } from 'shared/events/bub_events';
import classNames from 'classnames';
import { graphQL } from 'javascripts/shared/graphql';
import { graphQLMutations } from 'javascripts/shared/graphql/mutations';
import { graphQLQueries } from 'javascripts/shared/graphql/queries';
import { useMediaQuery } from 'react-responsive';
import { useOnScreen } from 'javascripts/hooks/use-on-screen';
import { userData } from 'javascripts/shared/utils/user_data';
import { useQueryParam } from 'javascripts/hooks/use-query-param';
import { BrowserAbilities } from 'javascripts/shared/utils/browser_abilities';
import { siteLinkData } from 'javascripts/shared/utils/site_link_data';

import { AssetImg } from 'javascripts/components/asset_img';
import { AudiobookButtons } from 'javascripts/components/audiobook/audiobook_buttons';
import { AudiobookCreditNames } from 'javascripts/components/audiobook/audiobook_credit_names';
import { AudiobookDescriptionSection } from 'javascripts/components/audiobook/audiobook_description_section';
import { AudiobookDetailsSection } from 'javascripts/components/audiobook/audiobook_details_section';
import { AudiobookDetailGrid } from 'javascripts/components/audiobook/audiobook_detail_grid';
import { AudiobookReviews } from 'javascripts/components/audiobook/audiobook_reviews';
import { AudiobookSupplementsSection } from 'javascripts/components/audiobook/audiobook_supplements_section';
import { BookCardCarouselWithGraphQLErrors } from 'javascripts/components/book_card_carousel_with_graphql_errors';
import { Breadcrumbs } from 'javascripts/components/breadcrumbs';
import { BundleCta } from 'javascripts/components/bundles/bundle_cta';
import { ChirpButton } from 'javascripts/components/chirp_button';
import { CollapsibleAudiobookSection } from 'javascripts/components/audiobook/collapsible_audiobook_section';
import { ComponentErrorBoundary } from 'javascripts/components/errors/component_error_boundary';
import { CoverImage } from 'javascripts/components/cover_image';
import { ExpiredPromotionModal } from 'javascripts/components/audiobook/expired_promotion_modal';
import { HowToListenSection } from 'javascripts/components/audiobook/how_to_listen_section';
import { GeneralSharingButton } from 'javascripts/components/general_sharing_button';
import { GiftAudiobookButton } from 'javascripts/components/gift_audiobook_button';
import { PlayAudiobookButton } from 'javascripts/components/play_audiobook_button';
import { PlaySampleButton } from 'javascripts/components/play_sample_button';
import { RecentlyViewedCarousel } from 'javascripts/components/recently_viewed_carousel';
import { SharedReviewModal } from 'javascripts/components/reviews/shared_review_modal';
import { StarRating } from 'javascripts/components/star_rating';
import { StickyCtaFooter } from 'javascripts/components/sticky_cta_footer';
import { AwardRibbon } from 'javascripts/components/award_ribbon';

import { ValuePropSection } from 'javascripts/components/value_prop_section';
import { WishlistButton } from 'javascripts/components/wishlist_button';

import roundGreenCheck from 'images/round_green_check';
import styles from 'stylesheets/apps/audiobook_app.module';

const MAX_CAROUSEL_ITEMS = 20;
const HIGHLIGHTED_CAROUSEL_SLIDE_COUNT = 4;

export function AudiobookApp({
  audiobook,
  audiobook: { product, userAudiobook },
  breadcrumbs,
  bundle,
  hasRelatedAudiobooks,
  isReferral,
  promoCodeName,
  relatedCarouselTags,
  referrerReview,
  showExpiredPromotionModal,
}) {
  // match breakpoints used in audiobook_app stylesheet
  const isMobile = useMediaQuery({ maxWidth: breakpoints.lg });
  const useSmallBookCards = useMediaQuery({ maxWidth: breakpoints.xl });
  const [bonusMaterialsSectionExpanded] = useQueryParam('bonus_materials', false);
  const [scrollToReviewsSection] = useQueryParam('reviews', false);
  const bonusMaterialsSectionRef = useRef();
  const reviewsSectionRef = useRef();

  const [expiredPromotionModalIsOpen, setExpiredPromotionModalIsOpen] = useState(showExpiredPromotionModal);
  const [inWishlist, setInWishlist] = useState(!_.isNil(audiobook.currentUserWishlistItem));

  // for referential equality in useGraphQLClientData hook (for carousels)
  const omitAudiobookIds = useMemo(() => [audiobook.id], [audiobook.id]);
  const productIsFree = useMemo(() => audiobook.product && (audiobook.product.free || audiobook.product.isFreeListing));
  const isFreeReferral = useMemo(() => isReferral && productIsFree);
  const socialSharingProps = useMemo(() => {
    if (audiobook.limitedTimeOffer && product) {
      const discountShareText = `I think you'd like this audiobook deal from Chirp! Only ${product.discountPrice} for a limited-time.`;

      return {
        emailShareTextOverride: discountShareText,
        pinterestShareTextOverride: `Check out this audiobook deal from Chirp! ${audiobook.displayTitle} by ${audiobook.displayAuthors} is only ${product.discountPrice} for a limited-time.`,
        twitterShareTextOverride: discountShareText,
        facebookShareTextOverride: discountShareText,
      };
    }

    return {
      emailShareTextOverride: "I think you'd like this audiobook from Chirp!",
      pinterestShareTextOverride: `Check out this audiobook from Chirp! ${audiobook.displayTitle} by ${audiobook.displayAuthors}`,
      twitterShareTextOverride: `I think you'd like this audiobook from Chirp: ${audiobook.displayTitle} by ${audiobook.displayAuthors}`,
      facebookShareTextOverride: "I think you'd like this audiobook from Chirp!",
    };
  });
  const nativeShareText = useMemo(() => {
    if (audiobook.limitedTimeOffer && product) {
      return `I think you'd like this audiobook deal from Chirp! Only ${product.discountPrice} for a limited-time.`;
    }

    return "I think you'd like this audiobook from Chirp!";
  });

  const ctaRef = useRef();
  const ctaVisible = useOnScreen(ctaRef);

  useEffect(() => {
    if (bonusMaterialsSectionExpanded && bonusMaterialsSectionRef.current) {
      bonusMaterialsSectionRef.current.focus();
    }

    if (!_.isEmpty(bundle)) {
      bubEvents.track('Bundle graphic', 'Viewed graphic', {
        label: 'Bundle id',
        value: bundle.id,
        bundle_name: bundle.identifier,
      });
    }
  }, []);

  useEffect(() => {
    if (scrollToReviewsSection && reviewsSectionRef.current) {
      reviewsSectionRef.current.scrollIntoView();
    }
  }, []);

  useEffect(() => {
    if (userData.isLoggedIn()) {
      graphQL.get(graphQLMutations.markAudiobookVisited, { audiobookId: audiobook.id });
    }
  }, []);

  function renderBreadcrumbs() {
    if (_.isEmpty(breadcrumbs)) {
      return null;
    }

    const levels = _.concat(breadcrumbs, { label: audiobook.displayTitle });

    return (
      <div className={styles.breadcrumbs}>
        <Breadcrumbs levels={levels} />
      </div>
    );
  }

  function showCoverButtons() {
    return !isMobile;
  }

  function renderCoverImage() {
    return (
      <div className={styles.coverWrapper}>
        <CoverImage
          coverUrl={audiobook.coverUrl}
          bookAuthor={audiobook.displayAuthors}
          bookId={audiobook.id}
          bookTitle={audiobook.displayTitle}
          defer={false}
          bannerType={audiobook.product?.bannerType}
        />
        {audiobook.sampleUrl ? (
          <div className={styles.playSampleButton}>
            <PlaySampleButton audiobook={audiobook} pillStyle trackingLocation='Audiobook details' />
          </div>
        ) : null}
      </div>
    );
  }

  function renderSeries() {
    if (_.isEmpty(audiobook.seriesName)) {
      return null;
    }

    const trackSeriesClick = () => {
      bubEvents.track('Series', 'Clicked link', {
        label: 'Part of a series',
        value: audiobook.seriesName,
      });
    };

    const seriesText = _.isNull(audiobook.seriesNumber)
      ? 'From the series: '
      : `Book ${audiobook.seriesNumber} from the series: `;

    return (
      <div className={styles.series} data-testid='audiobook-app-series-section'>
        {seriesText}
        <ChirpButton
          displayStyle='pink'
          displayType='link'
          href={audiobook.seriesUrl}
          label={audiobook.seriesName}
          onClick={trackSeriesClick}
        />
      </div>
    );
  }

  function renderTitleDetails() {
    return (
      <div className={styles.titleDetails} data-testid='audiobook-app-title-details'>
        {renderSeries()}
        <h1 className={styles.title}>{audiobook.displayTitle}</h1>
        {audiobook.subtitle ? <h2 className={styles.subtitle}>{audiobook.subtitle}</h2> : null}
        <div className={styles.byline}>
          By <AudiobookCreditNames credits={audiobook.authorCredits} type='author' />
        </div>
      </div>
    );
  }

  function renderValueProps() {
    if (userData.isLoggedIn()) {
      return null;
    }

    return (
      <div className={styles.valueProps}>
        <ValuePropSection displayType='dark' />
      </div>
    );
  }

  function renderAudiobookDetailGrid() {
    return (
      <div className={styles.detailGrid}>
        <h2 className={styles.detailGridHeading}>Audiobook Overview</h2>
        <AudiobookDetailGrid
          authors={audiobook.authorCredits}
          narrators={audiobook.narratorCredits}
          runtime={audiobook.runtime}
          dark
        />
      </div>
    );
  }

  function renderAverageStarRating() {
    if (audiobook.ratingsCount < 5 || !_.isFinite(audiobook.averageRating)) {
      if (isMobile) {
        return null;
      }
      return <div className={styles.averageRatingSpacer} data-testid='audiobook-app-rating-spacer'></div>;
    }

    return (
      <div className={styles.averageRatingWrapper}>
        <StarRating
          rating={audiobook.averageRating}
          ratingsCount={audiobook.ratingsCount}
          ratingsCountLink='#reviews'
          showRating
          showRatingsCount
          size={isMobile ? 'large' : 'xlarge'}
          textSize={isMobile ? 'small' : 'medium'}
        />
      </div>
    );
  }

  function renderPrices() {
    if (!product) {
      return null;
    }

    if (product.free || product.isFreeListing) {
      return (
        <div className={styles.prices}>
          {promoCodeName ? 'YOUR PRICE: ' : null}
          <span className={styles.discountPrice} aria-label='Discount price'>
            Free
          </span>
        </div>
      );
    }

    return (
      <div className={styles.prices}>
        {promoCodeName ? 'YOUR PRICE: ' : null}
        {product.showListingPrice ? (
          <span className={styles.listPrice} aria-hidden='true'>
            {product.listingPrice}
          </span>
        ) : null}
        <span className={styles.discountPrice} aria-label='Discount price'>
          {product.discountPrice}
        </span>
      </div>
    );
  }

  function renderTimeLeft(limitedTimeOfferText) {
    if (isMobile) {
      if (product.timeLeft) {
        return (
          <div className={styles.timeLeft} data-testid='audiobook-app-time-left'>
            <b>{limitedTimeOfferText}</b>
            {product.timeLeft !== limitedTimeOfferText ? ` - ${product.timeLeft}` : null}
          </div>
        );
      }

      return (
        <div className={styles.timeLeft} data-testid='audiobook-app-time-left'>
          <b>{limitedTimeOfferText}</b>
        </div>
      );
    }

    if (product.timeLeft) {
      return product.timeLeft !== limitedTimeOfferText ? (
        <div className={styles.timeLeft} data-testid='audiobook-app-time-left'>
          {product.timeLeft}
        </div>
      ) : null;
    }

    return null;
  }

  function renderPricingDetails() {
    if (!product || !_.isEmpty(userAudiobook)) {
      return null;
    }

    if (!audiobook.limitedTimeOffer || !audiobook.product.salableInCurrentCountry) {
      return (
        <div className={styles.pricing} data-testid='audiobook-app-audiobook-prices'>
          {renderPrices()}
        </div>
      );
    }

    const limitedTimeOfferText = 'Limited-time offer';

    return (
      <div className={styles.pricing} data-testid='audiobook-app-audiobook-prices'>
        {promoCodeName || isMobile ? null : <div className={styles.limitedTimeOffer}>{limitedTimeOfferText}</div>}
        {renderPrices()}
        {renderTimeLeft(limitedTimeOfferText)}
      </div>
    );
  }

  function renderGiftAudiobookButton() {
    return <GiftAudiobookButton audiobookId={audiobook.id} giftPurchaseUrl={product.giftPurchaseUrl} condensed />;
  }

  function renderShareButton() {
    return <GeneralSharingButton
            audiobook={audiobook}
            small
            nativeShareText={nativeShareText}
            socialSharingProps={socialSharingProps}
          />;
  }

  function renderSpacer() {
    return (
      <div className={styles.spacerWrapper}>
        <div className={styles.spacer}></div>
      </div>
    );
  }

  function userAudiobookButtons() {
    if (!product) {
      return null;
    }

    if (!(product.free || product.isFreeListing) && product.salableInCurrentCountry) {
      return (
        <div className={classNames(styles.buttonRow, styles.grid)}>
          <div className={styles.buttonColumn}>{renderGiftAudiobookButton()}</div>
          {renderSpacer()}
          <div className={styles.buttonColumn}>{renderShareButton()}</div>
        </div>
      );
    }

    return (
      <div className={classNames(styles.buttonRow)}>
        <div className={styles.buttonColumn}>{renderShareButton()}</div>
      </div>
    );
  }

  function renderGiftAudiobookSection() {
    return (
      <div className={styles.ownedBookDetails}>
        <span className={styles.shareBookText}>Gift this book to your friends!</span>
        <div className={styles.currentPrice}>
          <span>Current price:&nbsp;</span>
          <div className={styles.pricing}>{renderPrices()}</div>
        </div>
      </div>
    );
  }

  function buyNowCtaText() {
    if (isFreeReferral) {
      return 'Redeem Your Free Audiobook';
    }

    if (product.discounted && userData.isLoggedIn()) {
      return 'Buy Now';
    }

    return 'Buy Audiobook';
  }

  function renderAudiobookButtons() {
    if (isReferral) {
      return (
        <div className={styles.referralNoSubscriptionText}>
          No subscription fees. Ever.
        </div>
      );
    }

    if (!product.salableInCurrentCountry) {
      return null;
    }

    return (
      <AudiobookButtons
        audiobookId={audiobook.id}
        giftPurchaseUrl={product.giftPurchaseUrl}
        currentlyFree={product.free || product.isFreeListing}
        socialSharingProps={socialSharingProps}
        nativeShareText={nativeShareText}
      />
    );
  }

  function renderCtas(saleable) {
    if (!_.isEmpty(userAudiobook)) {
      return (
        <>
          <span className={styles.ownedBook}>You own this Audiobook</span>
          <div className={classNames(styles.ctas, styles.ownedBookCtas)}>
            <PlayAudiobookButton userAudiobook={userAudiobook} />
            {saleable && renderGiftAudiobookSection()}
            {saleable && userAudiobookButtons()}
          </div>
        </>
      );
    }

    if (audiobook.futureSaleDate) {
      return (
        <div className={styles.ctas}>
          <WishlistButton
            audiobookId={audiobook.id}
            buttonStyle='defaultYellow'
            inCart={false}
            inWishlist={inWishlist}
            onClick={() => setInWishlist(!inWishlist)}
            trackingCategory='Audiobook details'
          />
        </div>
      );
    }

    if (!product) {
      return null;
    }

    const trackBuyButtonClicks = () => {
      bubEvents.track('Audiobook details', 'Clicked Get Deal button', {
        label: 'Audiobook id',
        value: audiobook.id,
      });

      if (window.gtag !== undefined) {
        gtag('event', 'buy_now', {
          event_category: 'purchase',
          event_label: audiobook.displayTitle,
        });
      }
    };

    return (
      <div className={styles.ctas} ref={ctaRef}>
        <ChirpButton
          attrs={{ rel: 'nofollow' }}
          class={styles.buyButton}
          displayStyle='yellow'
          fullWidth
          href={product.purchaseUrl}
          label={buyNowCtaText()}
          onClick={trackBuyButtonClicks}
        />
        {renderAudiobookButtons()}
      </div>
    );
  }

  function renderNewMemberPromoCodeText() {
    return (
      <div className={styles.newMemberPromoTextArea}>
        <AssetImg src={roundGreenCheck} attrs={{ className: styles.greenCheck, alt: '' }} />
        <div className={styles.newMemberPromoText}>
          {' '}
          Promo code <strong>{promoCodeName}</strong> has been applied.
        </div>
      </div>
    );
  }

  function renderViewAllBundlesLink() {
    const viewAllLinkData = { text: 'Browse All Available Bundles', url: siteLinkData.getPath('bundlesIndex') };

    const trackViewAllBundleClick = () => {
      bubEvents.track('Bundle graphic', 'Clicked link', {
        label: viewAllLinkData.text,
      });
    };

    return (
      <ChirpButton
        class={styles.viewAllBundlesLink}
        displayStyle='underline'
        href={viewAllLinkData.url}
        label={viewAllLinkData.text}
        onClick={trackViewAllBundleClick}
      />
    );
  }

  function renderBundleImage() {
    if (_.isEmpty(bundle)) {
      return null;
    }

    const trackBundleClick = () => {
      bubEvents.track('Bundle graphic', 'Clicked graphic', {
        label: 'Bundle id',
        value: bundle.id,
        bundle_name: bundle.identifier,
      });
    };

    if (bundle.cloudinaryUrl) {
      return (
        <>
          <a href={bundle.clickthroughPath} onClick={trackBundleClick} data-testid='bundle-image-link'>
            <img className={styles.bundleImage} src={bundle.cloudinaryUrl} alt={bundle.altText} />
          </a>
          {renderViewAllBundlesLink()}
        </>
      );
    }

    return (
      <>
        <a className={styles.bundleImage} href={bundle.clickthroughPath} onClick={trackBundleClick} data-testid='bundle-cta-link'>
          <BundleCta
            bundle={bundle}
          />
        </a>
        {renderViewAllBundlesLink()}
      </>
    );
  }

  function renderPurchasingSection() {
    if (audiobook.futureSaleDate) {
      return (
        <>
          <div className={styles.futureSaleDate}>
            <div className={styles.releaseDateText}>
              <span className={styles.releaseDateLabel}>Release Date:</span>
              <span>{audiobook.futureSaleDate}</span>
            </div>
            <div className={styles.notifyText}>Get notified when this book becomes available.</div>
          </div>
          {renderCtas(false)}
        </>
      );
    }

    if (_.isEmpty(product) || !audiobook.fitForSale) {
      return (
        <div>
          {!_.isEmpty(userAudiobook) && renderCtas(false)}
          <div className={styles.notAvailable}>Audiobook not available for purchase</div>
        </div>
      );
    }

    return (
      <div>
        {renderPricingDetails()}
        {promoCodeName ? renderNewMemberPromoCodeText() : null}
        {renderCtas(true)}
        {renderBundleImage()}
      </div>
    );
  }

  function renderBlurbSection() {
    return (
      <section className={styles.blurbSection} aria-label='Audiobook description'>
        <AudiobookDescriptionSection audiobook={audiobook} />
      </section>
    );
  }

  function renderPopularDealsCarousel(showCarouselCoverButtons, useSmallText) {
    const carouselTitle = 'Popular Deals';
    const trackingCategory = `Audiobook Page ${carouselTitle} Carousel`;

    const queryParams = {
      page: 1,
      pageSize: MAX_CAROUSEL_ITEMS,
      omitAudiobookIds,
    };

    return (
      <div className={styles.highlightedCarouselSection}>
        <BookCardCarouselWithGraphQLErrors
          fetchResponseItems={response => response.popularPromotions}
          query={graphQLQueries.popularPromotions}
          queryParams={queryParams}
          responseDataType='promotion'
          title={<h2 className={styles.highlightedCarouselTitle}>{carouselTitle}</h2>}
          trackingCategory={trackingCategory}
          slideSize={HIGHLIGHTED_CAROUSEL_SLIDE_COUNT}
          arrowTrackingCategory={trackingCategory}
          emptyMessage='No books currently available'
          useSmallText={useSmallText}
          showCoverButtons={showCarouselCoverButtons}
          showStarRatings
          useResizable
          hideEmptyCarousel
        />
      </div>
    );
  }

  function renderRelatedBooksCarousel(showCarouselCoverButtons, useSmallText, carouselVerticalPosition) {
    const trackingCategory = 'Audiobook Page From the Same Author Carousel';

    return (
      <div className={styles.highlightedCarouselSection}>
        <BookCardCarouselWithGraphQLErrors
          fetchResponseItems={response => response.audiobook.relatedAudiobooks}
          query={graphQLQueries.relatedAudiobooks}
          queryParams={{
            id: audiobook.id,
            count: MAX_CAROUSEL_ITEMS,
          }}
          responseDataType='audiobook'
          title={<h2 className={styles.highlightedCarouselTitle}>From the Same Author</h2>}
          trackingCategory={trackingCategory}
          trackingProperties={{ carousel_vertical_position: carouselVerticalPosition }}
          slideSize={HIGHLIGHTED_CAROUSEL_SLIDE_COUNT}
          arrowTrackingCategory={trackingCategory}
          showCoverButtons={showCarouselCoverButtons}
          showStarRatings
          useSmallText={useSmallText}
          useResizable
          hideEmptyCarousel
        />
      </div>
    );
  }

  function trackViewAllClick(trackingCategory) {
    bubEvents.track(trackingCategory, 'Clicked link', {
      label: 'View All',
    });
  }

  function renderTagCarousel(tag, useSmallText, carouselPosition, highlighted) {
    const trackingCategory = `Audiobook Page ${tag.displayName} Carousel`;
    const carouselTitle = `Deals in ${tag.displayName}`;
    const styleClass = highlighted ? styles.highlightedCarouselSection : styles.carousel;
    const title = highlighted ? (
      <h2 className={styles.highlightedCarouselTitle} aria-hidden='true'>
        {carouselTitle}
      </h2>
    ) : (
      carouselTitle
    );
    return (
      <div className={styleClass} key={`${tag.slug}-carousel`} data-testid={`audiobook-app-tag-carousel-${tag.id}`}>
        <BookCardCarouselWithGraphQLErrors
          fetchResponseItems={response => _.get(response.tag, 'promotions') || []}
          query={graphQLQueries.tagPromotions}
          queryParams={{
            tagSlug: tag.slug,
            sortKey: tag.sort,
            page: 1,
            pageSize: MAX_CAROUSEL_ITEMS,
            excludeAudiobookId: audiobook.id,
          }}
          responseDataType='promotion'
          title={title}
          trackingCategory={trackingCategory}
          trackingProperties={{ carousel_vertical_position: carouselPosition }}
          linkHref={tag.url}
          linkText='View All'
          linkTracker={() => trackViewAllClick(trackingCategory)}
          arrowTrackingCategory={trackingCategory}
          hideEmptyCarousel
          showCoverButtons
          showStarRatings
          slideSize={highlighted ? HIGHLIGHTED_CAROUSEL_SLIDE_COUNT : null}
          useSmallText={useSmallText}
        />
      </div>
    );
  }

  function renderHighlightedCarousel() {
    // For this particular carousel, the buttons get too small to show the buttons
    // at around the same breakpoint we shift the text size
    const showCarouselCoverButtons = showCoverButtons() && !useSmallBookCards;
    const useSmallText = useSmallBookCards && !isMobile;

    if (hasRelatedAudiobooks) {
      return renderRelatedBooksCarousel(showCarouselCoverButtons, useSmallText, 1);
    }

    if (!_.isEmpty(relatedCarouselTags)) {
      return renderTagCarousel(relatedCarouselTags[0], useSmallText, 1, true);
    }

    return renderPopularDealsCarousel(showCarouselCoverButtons, useSmallText);
  }

  function renderAudiobookDetails() {
    return (
      <div className={styles.audiobookDetailsSection}>
        <AudiobookDetailsSection audiobook={audiobook} />
      </div>
    );
  }

  function renderReviewsSection() {
    if (_.isEmpty(userAudiobook) && !audiobook.hasReviews) {
      return null;
    }

    return (
      <section className={styles.reviewsSection} aria-label='User reviews' ref={reviewsSectionRef}>
        <AudiobookReviews audiobookId={audiobook.id} hasUserPurchased={!_.isEmpty(userAudiobook)} />
      </section>
    );
  }

  function renderRelatedCarouselsSection() {
    // if there were no related audiobooks, the first related tag carousel was already shown as the highlighted carousel
    const relatedTags = hasRelatedAudiobooks ? relatedCarouselTags : relatedCarouselTags.slice(1);
    if (_.isEmpty(relatedTags)) {
      return null;
    }

    return (
      <section className={styles.relatedCarouselsSection} aria-labelledby='related-carousels-section-header'>
        <h2 className={styles.relatedCarouselsHeader} id='related-carousels-section-header'>
          You may also like...
        </h2>
        {relatedTags.map((tag, index) => renderTagCarousel(tag, false, index + 2, false))}
      </section>
    );
  }

  function renderRecentlyViewedCarousel() {
    if (!userData.isLoggedIn()) {
      return null;
    }

    const carouselVerticalPosition = hasRelatedAudiobooks ? relatedCarouselTags.length + 1 : relatedCarouselTags.length;

    return (
      <ComponentErrorBoundary appName='Recently Viewed Carousel' hideOnError>
        <RecentlyViewedCarousel
          omitAudiobookIds={omitAudiobookIds}
          trackingProperties={{ carousel_vertical_position: carouselVerticalPosition }}
        />
      </ComponentErrorBoundary>
    );
  }

  function renderStickyCtaFooter() {
    return (
      <ComponentErrorBoundary appName='Sticky Cta Footer' hideOnError>
        <StickyCtaFooter
          audiobookId={audiobook.id}
          buttonLabel={isFreeReferral ? 'Redeem Your Free Audiobook' : 'Add to Cart'}
        />
      </ComponentErrorBoundary>
    );
  }

  function renderStickyFooter() {
    if (
      isMobile &&
      _.isEmpty(audiobook.futureSaleDate) &&
      _.isEmpty(userAudiobook) &&
      (!ctaVisible || !BrowserAbilities.canUseIntersectionObservers())
    ) {
      return renderStickyCtaFooter();
    }

    return null;
  }

  function renderSupplementalMaterialsSection() {
    if (_.isEmpty(audiobook.supplements)) {
      return null;
    }

    return (
      <div tabIndex='0' ref={bonusMaterialsSectionRef}>
        <CollapsibleAudiobookSection title='Bonus Materials' toggle={bonusMaterialsSectionExpanded}>
          <AudiobookSupplementsSection
            supplements={audiobook.supplements}
            hasUserPurchased={!_.isEmpty(userAudiobook)}
          />
        </CollapsibleAudiobookSection>
      </div>
    );
  }

  function renderHowToListenSection() {
    return (
      <CollapsibleAudiobookSection title='How to Listen'>
        <HowToListenSection />
      </CollapsibleAudiobookSection>
    );
  }

  function renderCollapsiblesSection() {
    return (
      <div className={styles.collapsiblesSection}>
        <div className={styles.collapsibleSection}>{renderSupplementalMaterialsSection()}</div>
        <div className={styles.collapsibleSection}>{renderHowToListenSection()}</div>
      </div>
    );
  }

  function renderExpiredPromotionModal() {
    if (!showExpiredPromotionModal) {
      return null;
    }

    return (
      <ExpiredPromotionModal
        audiobook={audiobook}
        isOpen={expiredPromotionModalIsOpen}
        onRequestClose={() => setExpiredPromotionModalIsOpen(false)}
      />
    );
  }

  function renderSharedReviewModal() {
    if (_.isEmpty(referrerReview)) {
      return null;
    }

    bubEvents.track('Audiobook page', 'Viewed review modal', {
      label: 'Review id',
      value: referrerReview.id,
    });

    return (
      <SharedReviewModal
        audiobookId={audiobook.id}
        authorCredits={audiobook.authorCredits}
        coverUrl={audiobook.coverUrl}
        displayAuthors={audiobook.displayAuthors}
        displayTitle={audiobook.displayTitle}
        free={product.free}
        purchaseUrl={product.purchaseUrl}
        userNamePossessive={referrerReview.userNamePossessive}
        userRating={referrerReview.rating}
        userReviewId={referrerReview.id}
        userReviewText={referrerReview.text}
      />
    );
  }

  function renderCurrentlyFeaturedInSection() {
    if (!audiobook.inAwardWinnersBookList || !audiobook.awardWinnersPageLink || !userData.isLoggedIn()) {
      return null;
    }

    return (
      <div className={styles.currentlyFeaturedInSection}>
        <h2 className={styles.currentlyFeaturedInHeading}>Currently Featured In:</h2>
        <div className={styles.awardRibbonWrapper}>
          <AwardRibbon
            heading='Award Winners Sale - Best of the Best'
            viewAllLink={audiobook.awardWinnersPageLink}
            trackingCategory='Awards Sale audiobook page promotion'
            trackingProperties={{
              label: 'Audiobook id',
              value: audiobook.id,
            }}
          />
        </div>
      </div>
    );
  }

  if (isMobile) {
    return (
      <div className={styles.audiobookApp}>
        {renderBreadcrumbs()}
        <div className={styles.audiobookContent} data-testid='audiobook-app-mobile-content'>
          {renderTitleDetails()}
          {renderAverageStarRating()}
          {renderCoverImage()}
          {renderPurchasingSection()}
          {renderValueProps()}
          {renderCurrentlyFeaturedInSection()}
          {renderAudiobookDetailGrid()}
          {renderBlurbSection()}
          {renderAudiobookDetails()}
          {renderHighlightedCarousel()}
          {renderReviewsSection()}
          {renderCollapsiblesSection()}
          {renderRelatedCarouselsSection()}
          {renderRecentlyViewedCarousel()}
        </div>
        {renderStickyFooter()}
        {renderExpiredPromotionModal()}
        {renderSharedReviewModal()}
      </div>
    );
  }

  return (
    <div className={styles.audiobookApp}>
      {renderBreadcrumbs()}
      <div className={styles.audiobookContent}>
        <div className={styles.desktopSide}>
          {renderCoverImage()}
          {renderPurchasingSection()}
        </div>
        <div className={styles.desktopContent} data-testid='audiobook-app-desktop-content'>
          {renderTitleDetails()}
          {renderAverageStarRating()}
          {renderValueProps()}
          {renderCurrentlyFeaturedInSection()}
          {renderAudiobookDetailGrid()}
          {renderBlurbSection()}
          {renderAudiobookDetails()}
          {renderHighlightedCarousel()}
          {renderReviewsSection()}
          {renderCollapsiblesSection()}
        </div>
        <div className={styles.desktopFull}>
          {renderRelatedCarouselsSection()}
          {renderRecentlyViewedCarousel()}
        </div>
      </div>
      {renderStickyFooter()}
      {renderExpiredPromotionModal()}
      {renderSharedReviewModal()}
    </div>
  );
}

AudiobookApp.propTypes = {
  audiobook: PropTypes.shape({
    abridged: PropTypes.bool.isRequired,
    authorCredits: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string.isRequired,
        url: PropTypes.string.isRequired,
      }),
    ).isRequired,
    averageRating: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    bookDescription: PropTypes.string.isRequired,
    bookDescriptionShort: PropTypes.string,
    coverUrl: PropTypes.string.isRequired,
    currentUserShoppingCartItem: PropTypes.shape({
      id: PropTypes.string.isRequired,
    }),
    currentUserWishlistItem: PropTypes.shape({
      id: PropTypes.string.isRequired,
    }),
    displayAuthors: PropTypes.string.isRequired,
    displayTitle: PropTypes.string.isRequired,
    editorialDescription: PropTypes.string,
    featuredLists: PropTypes.arrayOf(
      PropTypes.shape({
        title: PropTypes.string.isRequired,
        url: PropTypes.string.isRequired,
      }),
    ),
    fitForSale: PropTypes.bool.isRequired,
    futureSaleDate: PropTypes.string,
    hasReviews: PropTypes.bool.isRequired,
    id: PropTypes.string.isRequired,
    limitedTimeOffer: PropTypes.bool.isRequired,
    narratorCredits: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string.isRequired,
        url: PropTypes.string.isRequired,
      }),
    ).isRequired,
    primaryCategory: PropTypes.shape({
      displayName: PropTypes.string.isRequired,
      internalName: PropTypes.string.isRequired,
    }),
    product: PropTypes.shape({
      discounted: PropTypes.bool.isRequired,
      discountPrice: PropTypes.string,
      free: PropTypes.bool,
      giftPurchaseUrl: PropTypes.string,
      isFreeListing: PropTypes.bool.isRequired,
      listingPrice: PropTypes.string,
      purchaseUrl: PropTypes.string,
      salableInCurrentCountry: PropTypes.bool,
      showListingPrice: PropTypes.bool,
      timeLeft: PropTypes.string,
      bannerType: PropTypes.bool,
    }),
    publisher: PropTypes.string.isRequired,
    ratingsCount: PropTypes.number.isRequired,
    releaseDate: PropTypes.string.isRequired,
    runtime: PropTypes.string.isRequired,
    sampleUrl: PropTypes.string,
    seriesName: PropTypes.string,
    seriesNumber: PropTypes.string,
    seriesUrl: PropTypes.string,
    subtitle: PropTypes.string,
    supplements: PropTypes.arrayOf(
      PropTypes.shape({
        displayName: PropTypes.string.isRequired,
        fileType: PropTypes.string.isRequired,
        filePath: PropTypes.string.isRequired,
      }),
    ),
    tags: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        displayName: PropTypes.string.isRequired,
        url: PropTypes.string.isRequired,
      }),
    ),
    url: PropTypes.string.isRequired,
    userAudiobook: PropTypes.shape({
      archived: PropTypes.bool.isRequired,
      playable: PropTypes.bool.isRequired,
      playerUrl: PropTypes.string,
    }),
    inAwardWinnersBookList: PropTypes.bool,
    awardWinnersPageLink: PropTypes.string,
  }).isRequired,
  breadcrumbs: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      url: PropTypes.string,
    }),
  ),
  bundle: PropTypes.shape({
    altText: PropTypes.string,
    clickthroughPath: PropTypes.string,
    cloudinaryUrl: PropTypes.string,
    id: PropTypes.string,
    identifier: PropTypes.string,
    bundlePrice: PropTypes.string,
    discountAmount: PropTypes.string,
    tagline: PropTypes.string,
    covers: PropTypes.arrayOf(
      PropTypes.shape({
        coverUrl: PropTypes.string,
        displayAuthors: PropTypes.string,
        displayTitle: PropTypes.string,
      }),
    ),
  }),
  hasRelatedAudiobooks: PropTypes.bool.isRequired,
  isReferral: PropTypes.bool,
  relatedCarouselTags: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      slug: PropTypes.string,
      url: PropTypes.string,
      sort: PropTypes.string,
    }),
  ),
  referrerReview: PropTypes.shape({
    text: PropTypes.string,
    id: PropTypes.string,
    rating: PropTypes.number,
    userNamePossessive: PropTypes.string,
  }),
  showExpiredPromotionModal: PropTypes.bool.isRequired,
  promoCodeName: PropTypes.string,
};

AudiobookApp.defaultProps = {
  bundle: {},
  hasRelatedAudiobooks: false,
  isReferral: false,
  relatedCarouselTags: [],
};
